import {Filter, type FilterQuery, SubmitEvent, ValidationMessage} from '@github-ui/filter'
import {
  type AnyBlock,
  BlockType,
  FILTER_KEYS,
  type FilterConfig,
  getLastFilterBlockValue,
  type MutableFilterBlock,
} from '@github-ui/filter-core'
import {KeyOnlyFilterProvider, SelectFilterProvider} from '@github-ui/filter-core/providers'
import {useDebounce} from '@github-ui/use-debounce'
import {useSyncedState} from '@github-ui/use-synced-state'
import {GitBranchIcon, KeyIcon} from '@primer/octicons-react'
import {useCallback, useId, useMemo, useState} from 'react'
import {WorkflowRunsAsyncFilterProvider} from '../workflow-runs/workflow-runs-filter-providers'
import {cacheSortOptions} from './cache-filter-options'
import styles from './CachesList.module.css'

const VALIDATION_MESSAGE_DEBOUNCE_MS = 300

const IGNORED_TERMS_MESSAGE = 'Unrecognized search terms will be ignored. Use key, branch, or sort filters.'
const sanitizeMessage = (message: string) => (message.includes(IGNORED_TERMS_MESSAGE) ? '' : message)

const singleValueOptions = {
  filterTypes: {valueless: false, hasValue: false, multiValue: false, multiKey: false, exclusive: false},
} as const

export function CachesFilter({
  branchesPath,
  query,
  onQueryChange,
  track,
}: {
  branchesPath: string
  query: string
  onQueryChange: (query: string) => void
  track: (action: string) => void
}) {
  const id = useId()
  const [filterValue, setFilterValue] = useSyncedState(query)
  const providers = useMemo(
    () => [
      new KeyOnlyFilterProvider(
        {key: 'key', displayName: 'Key', description: 'Filter by cache key', icon: KeyIcon, priority: 1},
        {...singleValueOptions, caseSensitiveValues: true, priority: 1},
      ),
      new CacheBranchSuggestionsFilterProvider(
        {key: 'branch', displayName: 'Branch', description: 'Filter by branch', icon: GitBranchIcon, priority: 2},
        branchesPath,
        {...singleValueOptions, caseSensitiveValues: true, priority: 2},
      ),
      new SelectFilterProvider(
        FILTER_KEYS.sort,
        cacheSortOptions.map((option, index) => ({value: option.id, displayName: option.text, priority: index + 1})),
        {...singleValueOptions, priority: 3},
      ),
    ],
    [branchesPath],
  )
  const handleChange = useCallback(
    (value: string) => {
      setFilterValue(value)
      if (!value.trim() && query) {
        track('clear_search')
        onQueryChange('')
      }
    },
    [onQueryChange, query, setFilterValue, track],
  )
  const handleSubmit = useCallback(
    (filterQuery: FilterQuery, eventType: SubmitEvent) => {
      if (eventType === SubmitEvent.Clear) return
      track('search')
      onQueryChange(filterQuery.raw)
    },
    [onQueryChange, track],
  )

  const validationMessageId = `${id}-validation-message`
  const [validationMessages, setValidationMessages] = useState<string[]>([])
  const showValidationMessages = useDebounce(setValidationMessages, VALIDATION_MESSAGE_DEBOUNCE_MS)
  const handleValidation = useCallback(
    (messages: string[], filterQuery: FilterQuery) => {
      const hasIgnoredTerms = filterQuery.blocks.some(block => block.type === BlockType.Text)
      const nextMessages = hasIgnoredTerms ? [...messages, IGNORED_TERMS_MESSAGE] : messages
      if (nextMessages.length === 0) {
        showValidationMessages.cancel()
        setValidationMessages([])
      } else {
        showValidationMessages(nextMessages)
      }
    },
    [showValidationMessages],
  )
  const handleSubmitWithValidation = useCallback(
    (filterQuery: FilterQuery, eventType: SubmitEvent) => {
      showValidationMessages.flush()
      handleSubmit(filterQuery, eventType)
    },
    [handleSubmit, showValidationMessages],
  )

  return (
    <>
      <Filter
        id={id}
        label="Filter caches"
        placeholder="Filter caches"
        variant="input"
        providers={providers}
        filterValue={filterValue}
        onChange={handleChange}
        onSubmit={handleSubmitWithValidation}
        onValidation={handleValidation}
        showValidationMessage={false}
        settings={{groupAndKeywordSupport: false, showSuggestionsOnFocus: true}}
        aria-describedby={validationMessages.length > 0 ? validationMessageId : undefined}
      />
      <ValidationMessage
        messages={validationMessages}
        id={validationMessageId}
        sanitizeMessage={sanitizeMessage}
        className={styles.FilterValidationMessage}
      />
    </>
  )
}

class CacheBranchSuggestionsFilterProvider extends WorkflowRunsAsyncFilterProvider<string> {
  override async fetchSuggestions(_value: string) {
    // The cache endpoint returns every branch, so fetch once and filter locally.
    const branches = await super.fetchSuggestions('')
    return branches?.every(branch => typeof branch === 'string') ? [...new Set(branches)] : null
  }

  override async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    config: FilterConfig,
    caretIndex?: number | null,
  ) {
    const suggestions = await super.getSuggestions(filterQuery, filterBlock, config, caretIndex)
    const value = getLastFilterBlockValue(filterBlock, caretIndex).toLowerCase()
    return suggestions?.filter(suggestion => suggestion.displayName?.toLowerCase().includes(value)) ?? null
  }

  protected toSuggestion(branch: string) {
    return {value: branch, displayName: branch}
  }
}
